<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-podItem-actions-renamePod-activator">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-renamePod-modal">
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.pod.renamePodDialogTitle') }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-form ref="form" @submit.prevent="formSubmit()">
        <v-card-text class="pb-0">
          <v-text-field
            v-model="podName"
            :rules="podNameRules"
            :maxlength="podNameMaxLength"
            :label="$t('routes.pod.renamePodDialogFilename')"
            density="compact"
            variant="solo-filled"
            required
            flat
            data-cy="dialog-renamePod-podName"
          >
            <template v-if="podExtension" v-slot:append-inner>
              <span class="filename-extension text-medium-emphasis" aria-hidden="true">
                {{ podExtension }}
              </span>
            </template>
          </v-text-field>

          <div v-if="podNameError" class="bg-error">
            {{ podNameError }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            elevation="0"
            :disabled="isSubmitting"
            data-cy="dialog-renamePod-ok"
          >
            {{ $t('common.rename') }}
          </v-btn>
          <v-btn
            variant="text"
            @click="dialog = false"
            data-cy="dialog-renamePod-false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import FilenameHelper from '@/components/utils/FilenameHelper.js';

export default {
  mixins: [DialogActionKeys],
  name: "RenamePodDialog",
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    pod: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      podName: '',
      podNameRules: [
        (v) => !!v || this.$t('routes.pod.renamePodDialogInvalidName'),
        (v) => v && v.length <= this.podNameMaxLength || this.$t('routes.pod.fileNameTooLong'),
      ],
      podNameError: "",
      isSubmitting: false,
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.podName = this.podBaseName;
        this.podNameError = "";
        // Prevent double submission during dialog closure
        this.isSubmitting = false;
      }
    }
  },
  methods: {
    async formSubmit() {
      if (this.isSubmitting) {
        return;
      }

      const validForm = await this.$refs.form.validate();
      this.podNameError = "";

      if (!validForm.valid) {
        return;
      }

      const data = {
        id: this.pod.id,
        name: this.podName,
      };

      const podStore = usePodStore();
      this.isSubmitting = true;
      try {
        await podStore.renameFile(data);
        this.dialog = false;
        this.$showMessage(this.$t('routes.pod.fileRenamed'), { type: 'success' });
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.podNameError = error.response.data.error.message;
        } else {
          this.podNameError = "Error!";
          console.log(error);
        }

        this.isSubmitting = false;
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    podBaseName() {
      return FilenameHelper.getFilenameBase(this.pod?.name || '');
    },
    podExtension() {
      return FilenameHelper.getFilenameExtension(this.pod?.name || '');
    },
    podNameMaxLength() {
      return Math.max(0, 191 - this.podExtension.length);
    },
  }
};
</script>

<style lang="scss" scoped>
.filename-extension {
  white-space: nowrap;
  pointer-events: none;
}
</style>
